"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useSelector, useDispatch } from "react-redux"
import Countdown from "react-countdown"
import { Mail, Phone, Lock, LogOut, RefreshCw } from "lucide-react"
import { notify } from "../../../utility/Helper1"
import { userSelect } from "../../../redux/selectors/index"
import { authActions } from "../../../redux/actions"
import AuthHolder from "./AuthHolder"

const UserVerification = () => {
    const userData = useSelector(userSelect)
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const [emailCodeInput, setEmailCodeInput] = useState("")
    const [phoneCodeInput, setPhoneCodeInput] = useState("")
    const [verifyingEmailOrPhone, setVerifyingEmailOrPhone] = useState(0)
    const [loading, changeLoading] = useState(false)
    const [loadingText, setLoadingText] = useState("")
    const [fullPageLoading, setFullPageLoading] = useState(false)

    // Verify email function
    const verifyEmail = (e) => {
        e.preventDefault()
        if (loading) return

        changeLoading(true)
        setLoadingText("Please wait...")

        dispatch(
            authActions.onSignUp("account/verify_email", { emailCode: emailCodeInput }, (response) => {
                if (response.code == 1) {
                    if (userData?.emailVerification == 1 && userData?.phoneVerification == 1) {
                        navigate("/", { replace: true })
                    } else {
                        setVerifyingEmailOrPhone(1)
                    }
                }

                notify(response.msg, response.code)
                changeLoading(false)
                setLoadingText("")
            }),
        )
    }

    // Resend email verification
    const resendEmailVerification = (e) => {
        e.preventDefault()
        if (loading) return

        changeLoading(true)
        setLoadingText("Please wait...")

        dispatch(
            authActions.onRefreshSystemData("account/resend_email_verification", 100, {}, (response) => {
                notify(response.msg, response.code)
                changeLoading(false)
                setLoadingText("")
            }),
        )
    }

    // Resend phone verification
    const resendPhoneVerification = (e) => {
        e.preventDefault()
        if (loading) return

        changeLoading(true)
        setLoadingText("Please wait...")

        dispatch(
            authActions.onRefreshSystemData("account/resend_phone_verification", 100, {}, (response) => {
                notify(response.msg, response.code)
                setFullPageLoading(false)
                changeLoading(false)
            }),
        )
    }

    // Verify phone function
    const verifyPhone = (e) => {
        e.preventDefault()
        if (loading) return

        changeLoading(true)
        setLoadingText("Please wait...")

        dispatch(
            authActions.onSignUp("account/verify_phone", { smsCode: phoneCodeInput }, (response) => {
                if (response.code == 1) {
                    navigate("/", { replace: true })
                }

                notify(response.msg, response.code)
                changeLoading(false)
                setLoadingText("")
            }),
        )
    }

    // Logout function
    const doLogOutNowNow = () => {
        dispatch(authActions.onLogout())
    }

    // Set verification type based on user data
    useEffect(() => {
        if (userData?.emailVerification == 0) {
            setVerifyingEmailOrPhone(0)
        } else if (userData?.phoneVerification == 0) {
            setVerifyingEmailOrPhone(1)
        }
    }, [userData])

    return (
        <AuthHolder>
            <div className="max-w-md w-full mx-auto p-6">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                        {verifyingEmailOrPhone === 0 ? (
                            <Mail className="h-8 w-8 text-blue-600" />
                        ) : (
                            <Phone className="h-8 w-8 text-blue-600" />
                        )}
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-1">
                        {verifyingEmailOrPhone === 0 ? "Verify Your Email" : "Verify Your Phone"}
                    </h2>
                    <p className="text-gray-500">
                        {verifyingEmailOrPhone === 0
                            ? `We've sent a code to ${userData?.email}`
                            : `We've sent an SMS to (${userData?.countryPhoneCode})${userData?.phone}`}
                    </p>
                </div>

                {/* Verification Form */}
                {verifyingEmailOrPhone === 0 ? (
                    <form onSubmit={verifyEmail} className="space-y-6">
                        <div>
                            <label htmlFor="email-code" className="block text-sm font-medium text-gray-700 mb-1">
                                Verification Code
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Lock className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    id="email-code"
                                    type="text"
                                    placeholder="Enter 6-digit code"
                                    value={emailCodeInput}
                                    onChange={(e) => setEmailCodeInput(e.target.value)}
                                    required
                                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center font-mono text-lg tracking-wider"
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                            {loading ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Verifying...
                                </>
                            ) : (
                                "Verify Email"
                            )}
                        </button>

                        <div className="flex items-center justify-between pt-2 text-sm">
                            <div className="text-gray-500">
                                <Countdown
                                    zeroPadTime={2}
                                    key={new Date(userData?.lastEmailTime)}
                                    date={new Date(userData?.lastEmailTime)}
                                    renderer={({ minutes, seconds, completed }) => {
                                        if (completed) {
                                            return (
                                                <button
                                                    onClick={resendEmailVerification}
                                                    disabled={loading}
                                                    className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
                                                >
                                                    <RefreshCw className="h-4 w-4 mr-1.5" />
                                                    Resend Code
                                                    {loading && (
                                                        <svg className="animate-spin ml-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                    )}
                                                </button>
                                            )
                                        } else {
                                            return (
                                                <span className="flex items-center text-gray-500">
                                                    <RefreshCw className="h-4 w-4 mr-1.5 text-gray-400" />
                                                    Resend in{" "}
                                                    <span className="font-mono ml-1">
                                                        {minutes}:{seconds < 10 ? `0${seconds}` : seconds}
                                                    </span>
                                                </span>
                                            )
                                        }
                                    }}
                                />
                            </div>

                            <button
                                type="button"
                                onClick={doLogOutNowNow}
                                className="flex items-center text-gray-600 hover:text-gray-800 font-medium"
                            >
                                <LogOut className="h-4 w-4 mr-1.5" />
                                Sign Out
                            </button>
                        </div>
                    </form>
                ) : (
                    <form onSubmit={verifyPhone} className="space-y-6">
                        <div>
                            <label htmlFor="phone-code" className="block text-sm font-medium text-gray-700 mb-1">
                                SMS Verification Code
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Lock className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    id="phone-code"
                                    type="text"
                                    placeholder="Enter 6-digit code"
                                    value={phoneCodeInput}
                                    onChange={(e) => setPhoneCodeInput(e.target.value)}
                                    required
                                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center font-mono text-lg tracking-wider"
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                            {loading ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Verifying...
                                </>
                            ) : (
                                "Verify Phone"
                            )}
                        </button>

                        <div className="flex items-center justify-between pt-2 text-sm">
                            <div className="text-gray-500">
                                <Countdown
                                    zeroPadTime={2}
                                    key={new Date(userData?.lastSMSTime)}
                                    date={new Date(userData?.lastSMSTime)}
                                    renderer={({ minutes, seconds, completed }) => {
                                        if (completed) {
                                            return (
                                                <button
                                                    onClick={resendPhoneVerification}
                                                    disabled={loading}
                                                    className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
                                                >
                                                    <RefreshCw className="h-4 w-4 mr-1.5" />
                                                    Resend Code
                                                    {loading && (
                                                        <svg className="animate-spin ml-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                    )}
                                                </button>
                                            )
                                        } else {
                                            return (
                                                <span className="flex items-center text-gray-500">
                                                    <RefreshCw className="h-4 w-4 mr-1.5 text-gray-400" />
                                                    Resend in{" "}
                                                    <span className="font-mono ml-1">
                                                        {minutes}:{seconds < 10 ? `0${seconds}` : seconds}
                                                    </span>
                                                </span>
                                            )
                                        }
                                    }}
                                />
                            </div>

                            <button
                                type="button"
                                onClick={doLogOutNowNow}
                                className="flex items-center text-gray-600 hover:text-gray-800 font-medium"
                            >
                                <LogOut className="h-4 w-4 mr-1.5" />
                                Sign Out
                            </button>
                        </div>
                    </form>
                )}

                {/* Help Text */}
                {/* <div className="mt-8 p-4 bg-blue-50 rounded-lg">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            {verifyingEmailOrPhone === 0 ? (
                                <Mail className="h-5 w-5 text-blue-500" />
                            ) : (
                                <Phone className="h-5 w-5 text-blue-500" />
                            )}
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-blue-800">Having trouble?</h3>
                            <div className="mt-1 text-sm text-blue-700">
                                <p>
                                    {verifyingEmailOrPhone === 0
                                        ? "Check your spam folder if you don't see the email. The code will expire in 15 minutes."
                                        : "Make sure your phone can receive SMS messages. The code will expire in 10 minutes."}
                                </p>
                            </div>
                        </div>
                    </div>
                </div> */}
            </div>
        </AuthHolder>
    )
}

export default UserVerification